import React from "react";
import {
  ArrayField,
  Datagrid,
  FunctionField,
  SelectField,
  Show,
  SimpleShowLayout,
  TextField,
  useTranslate,
} from "react-admin";
import { Box, Divider, useMediaQuery } from "@mui/material";

import { Payment, ProductPayment } from "../api/payments";
import { AttachmentFileField } from "../components/AttachmentFile";
import { getFormatNumeric, formatStringAsCurrency } from "../utils/locale";
import {
  formatDisplayTradingVolume,
  formatDisplayUnitPrice,
  getDisplayTradingVolumeValue,
} from "../utils/price_calculation";
import {
  createDeliveryCategories,
  createDeliverySubcategories,
  createIsProductAdjustments,
} from "../components/choices";
import { UpdatePaymentButton } from "../components/UpdatePaymentButton";
import { MemoField } from "../components/MemoField";
import { commonStyles } from "../components/common_styles";
import { EmptyTitle } from "../components/EmptyTitle";

const getTotalTradingVolumeInYen = (record: Payment) => {
  const totalTradingVolumeInYen = record.product_payments.reduce(
    (sum, productPayment) =>
      sum +
      Number(
        getDisplayTradingVolumeValue({
          price_calculation_mode: record.price_calculation_mode,
          trading_volume_in_yen: productPayment.trading_volume_in_yen,
          trading_volume_excluding_tax_in_yen: productPayment.trading_volume_excluding_tax_in_yen,
        }) ?? 0
      ),
    0
  );
  return formatStringAsCurrency(totalTradingVolumeInYen.toString());
};

export const PaymentShow = (props: { permissions?: string | string[]; record?: Payment; resource?: string }) => {
  const { permissions, record, resource } = props;
  const translate = useTranslate();
  if (!record || !resource) {
    return null;
  }
  const hasWritePermission = permissions === "admin" || permissions === "staff";
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));
  const isTotalTradingVolumeInYen = record.product_payments.length > 1;
  const isTaxExcluded = record.price_calculation_mode === "exclusive";

  return (
    <Show {...props} title={<EmptyTitle />} resource="payments">
      <SimpleShowLayout
        sx={{
          "& .RaLabeled-label": {
            "font-size": "0.8rem",
          },
        }}
      >
        <FunctionField source="total_bill_in_yen" render={getFormatNumeric("total_bill_in_yen")} />
        <AttachmentFileField source="invoice" />
        <ArrayField source="attachments">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false} empty={null}>
              <AttachmentFileField label={false} />
            </Datagrid>
          </Box>
        </ArrayField>
        <Divider />
        <Box sx={{ fontSize: "0.8rem", color: "gray" }}>
          <Box>{`■${translate("resources.payments.fields.total_bill_in_yen")} = 【A】+【B】+【C】`}</Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" } }}>
            <Box sx={{ width: { xs: "auto", sm: "128px" } }}>
              {`【A : ${translate("resources.payments.fields.calculation_formula.product_payment_name")}】`}
            </Box>
            <Box>
              {`${translate("resources.payments.fields.product_payments")} + ${translate(
                "resources.payments.fields.adjustments"
              )}${translate("resources.payments.fields.calculation_formula.product_payment_adjustment")}`}
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" } }}>
            <Box sx={{ width: { xs: "auto", sm: "128px" } }}>
              {`【B : ${translate("resources.payments.fields.calculation_formula.commission_fee_name")}】`}
            </Box>
            <Box>{`${translate("resources.payments.fields.commission_fee_in_yen")} + ${translate(
              "resources.payments.fields.adjustments"
            )}${translate("resources.payments.fields.calculation_formula.commission_fee_adjustment")}`}</Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" } }}>
            <Box sx={{ width: { xs: "auto", sm: "128px" } }}>
              {`【C : ${translate("resources.payments.fields.calculation_formula.delivery_payment_name")}】`}
            </Box>
            <Box>{translate("resources.payments.fields.delivery_payments")}</Box>
            <Box sx={{ ml: { xs: 0, sm: 2 } }}>
              {translate("resources.payments.fields.calculation_formula.delivery_payment_caution")}
            </Box>
          </Box>
          <Box>{translate("resources.payments.fields.calculation_formula.tax_calculation_caution")}</Box>
        </Box>
        <ArrayField source="product_payments">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false} empty={null}>
              <TextField source="grade.name" emptyText={translate("ui.value.default_grade")} sortable={false} />
              {!isTaxExcluded && (
                <FunctionField source="tax_rate" render={getFormatNumeric("tax_rate")} sortable={false} />
              )}
              <FunctionField
                source="unit_price"
                render={(productPayment: ProductPayment) =>
                  formatDisplayUnitPrice({
                    price_calculation_mode: record.price_calculation_mode,
                    unit_price: productPayment.unit_price,
                    unit_price_excluding_tax_in_yen: productPayment.unit_price_excluding_tax_in_yen,
                  })
                }
                sortable={false}
              />
              <TextField source="sacks" />
              <FunctionField
                source="trading_volume_in_yen"
                render={(productPayment: ProductPayment) =>
                  formatDisplayTradingVolume({
                    price_calculation_mode: record.price_calculation_mode,
                    trading_volume_in_yen: productPayment.trading_volume_in_yen,
                    trading_volume_excluding_tax_in_yen: productPayment.trading_volume_excluding_tax_in_yen,
                  })
                }
                sx={{ display: "inline-block", width: "calc(10em - 32px)", wordBreak: "break-all" }}
                sortable={false}
              />
            </Datagrid>
            {isTotalTradingVolumeInYen && (
              <Box display="flex">
                <Box style={{ flex: 1, padding: "6px 0px", textAlign: "end" }}>小計</Box>
                <FunctionField
                  sx={{ flex: "none", padding: "6px 16px", width: "10em", wordBreak: "break-all" }}
                  render={getTotalTradingVolumeInYen}
                />
              </Box>
            )}
          </Box>
        </ArrayField>
        <ArrayField source="adjustments">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false} empty={null}>
              <TextField source="title" sortable={false} />
              <SelectField source="is_product_adjustment" choices={createIsProductAdjustments()} sortable={false} />
              {!isTaxExcluded && (
                <FunctionField source="tax_rate" render={getFormatNumeric("tax_rate")} sortable={false} />
              )}
              <FunctionField
                source="adjustment_in_yen"
                render={getFormatNumeric("adjustment_in_yen")}
                sortable={false}
              />
              <MemoField source="memo" sortable={false} />
            </Datagrid>
          </Box>
        </ArrayField>
        {!isTaxExcluded && (
          <FunctionField source="commission_fee_tax_rate" render={getFormatNumeric("commission_fee_tax_rate")} />
        )}
        <FunctionField source="commission_fee_in_yen" render={getFormatNumeric("commission_fee_in_yen")} />
        <ArrayField source="delivery_payments">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false} empty={null}>
              <SelectField source="category" choices={createDeliveryCategories()} sortable={false} />
              <SelectField source="subcategory" choices={createDeliverySubcategories()} sortable={false} />
              {!isTaxExcluded && (
                <FunctionField source="tax_rate" render={getFormatNumeric("tax_rate")} sortable={false} />
              )}
              <FunctionField source="fee_in_yen" render={getFormatNumeric("fee_in_yen")} sortable={false} />
            </Datagrid>
          </Box>
        </ArrayField>
        <Divider />
        {hasWritePermission && !isMobile && <UpdatePaymentButton />}
      </SimpleShowLayout>
    </Show>
  );
};
